import styled, { keyframes } from 'styled-components'

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.97) translateY(-6px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`

const backdropFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

export const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 10, 30, 0.82);
  backdrop-filter: blur(8px);
  z-index: 999999999;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${backdropFadeIn} 0.25s ease-out;
  padding: 16px;
  box-sizing: border-box;
`

export const ModalContainer = styled.div`
  background: ${({ theme }) => (theme.name === 'light' ? '#ffffff' : '#001e56')};
  border: 1px solid ${({ theme }) => (theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.16)')};
  box-shadow: ${({ theme }) =>
    theme.name === 'light'
      ? '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)'
      : '0 25px 50px -12px rgba(0, 0, 0, 0.75), 0 0 0 1px rgba(255, 255, 255, 0.05)'};
  border-radius: 20px;
  width: 100%;
  max-width: 900px;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${fadeIn} 0.28s cubic-bezier(0.16, 1, 0.3, 1);
  font-family: 'Montserrat', sans-serif;
  color: ${({ theme }) => (theme.name === 'light' ? '#0f172a' : '#ffffff')};
  position: relative;
`

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 26px;
  border-bottom: 1px solid ${({ theme }) => (theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.1)')};
  background: ${({ theme }) => (theme.name === 'light' ? '#f8fafc' : '#00153d')};

  .title-area {
    display: flex;
    align-items: center;
    gap: 12px;

    .icon-box {
      width: 40px;
      height: 40px;
      border-radius: 10px;
      background: rgba(166, 193, 11, 0.15);
      border: 1px solid rgba(166, 193, 11, 0.35);
      display: flex;
      align-items: center;
      justify-content: center;
      color: #a6c10b;
      font-size: 20px;
      flex-shrink: 0;
    }

    .title-texts {
      h2 {
        margin: 0;
        font-size: 17px;
        font-weight: 800;
        color: ${({ theme }) => (theme.name === 'light' ? '#0f172a' : '#ffffff')};
        display: flex;
        align-items: center;
        gap: 10px;
        flex-wrap: wrap;

        .step-pill {
          font-size: 11px;
          font-weight: 700;
          background: rgba(166, 193, 11, 0.2);
          color: #a6c10b;
          border: 1px solid rgba(166, 193, 11, 0.4);
          padding: 2px 10px;
          border-radius: 20px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
      }

      p {
        margin: 3px 0 0 0;
        font-size: 12px;
        color: ${({ theme }) => (theme.name === 'light' ? '#64748b' : '#94a3b8')};
      }
    }
  }

  .close-btn {
    background: transparent;
    border: none;
    cursor: pointer;
    color: ${({ theme }) => (theme.name === 'light' ? '#64748b' : '#94a3b8')};
    font-size: 22px;
    padding: 6px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;

    &:hover {
      color: ${({ theme }) => (theme.name === 'light' ? '#0f172a' : '#ffffff')};
      background: ${({ theme }) => (theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.1)')};
    }
  }
`

export const ModalBody = styled.div`
  padding: 24px 28px;
  overflow-y: auto;
  flex: 1;
  max-height: calc(92vh - 150px);
  box-sizing: border-box;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(166, 193, 11, 0.35);
    border-radius: 3px;
  }

  /* Hero Banner do Guia de Boas-Vindas */
  .hero-banner {
    background: ${({ theme }) =>
      theme.name === 'light'
        ? 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
        : 'linear-gradient(135deg, #002366 0%, #001233 100%)'};
    border: 1px solid ${({ theme }) => (theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.12)')};
    border-radius: 16px;
    padding: 24px 26px;
    box-shadow: ${({ theme }) =>
      theme.name === 'light' ? '0 4px 16px rgba(0, 0, 0, 0.05)' : '0 6px 20px rgba(0, 0, 0, 0.3)'};
    margin-bottom: 24px;
    position: relative;
    overflow: hidden;

    &::after {
      content: '';
      position: absolute;
      top: -50%;
      right: -10%;
      width: 320px;
      height: 320px;
      background: radial-gradient(circle, rgba(166, 193, 11, 0.15) 0%, transparent 70%);
      pointer-events: none;
    }

    .hero-top-badges {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
      margin-bottom: 12px;

      .badge {
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        padding: 3px 10px;
        border-radius: 20px;

        &.version {
          background: #a6c10b;
          color: #001842;
        }

        &.feature {
          background: ${({ theme }) => (theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.1)')};
          color: ${({ theme }) => (theme.name === 'light' ? '#0f172a' : '#cbd5e1')};
          border: 1px solid ${({ theme }) => (theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.15)')};
        }
      }
    }

    h1 {
      font-size: 22px;
      font-weight: 800;
      margin: 0 0 8px 0;
      color: ${({ theme }) => (theme.name === 'light' ? '#0f172a' : '#ffffff')};
      display: flex;
      align-items: center;
      gap: 10px;
      flex-wrap: wrap;

      .highlight {
        color: #a6c10b;
      }
    }

    p.lead-text {
      font-size: 13px;
      line-height: 1.6;
      color: ${({ theme }) => (theme.name === 'light' ? '#475569' : '#94a3b8')};
      max-width: 800px;
      margin: 0 0 18px 0;
    }

    .hero-quick-actions {
      display: flex;
      gap: 12px;
      flex-wrap: wrap;

      .action-btn {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 10px 20px;
        border-radius: 10px;
        font-family: 'Montserrat', sans-serif;
        font-size: 13px;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.2s ease;
        border: none;

        &.primary {
          background: #a6c10b;
          color: #001842;
          box-shadow: 0 4px 14px rgba(166, 193, 11, 0.35);

          &:hover {
            background: #b8d410;
            transform: translateY(-2px);
            box-shadow: 0 6px 18px rgba(166, 193, 11, 0.45);
          }
        }
      }
    }
  }

  h3.tab-headline {
    font-size: 16px;
    font-weight: 800;
    margin: 0 0 8px 0;
    color: ${({ theme }) => (theme.name === 'light' ? '#0f172a' : '#ffffff')};
    display: flex;
    align-items: center;
    gap: 8px;

    .icon-accent {
      color: #a6c10b;
      font-size: 20px;
    }
  }

  p.tab-subtitle {
    font-size: 13px;
    line-height: 1.6;
    color: ${({ theme }) => (theme.name === 'light' ? '#475569' : '#cbd5e1')};
    margin: 0 0 18px 0;
  }

  /* Bloco de Demonstração / Interativo */
  .interactive-demo-card {
    background: ${({ theme }) => (theme.name === 'light' ? '#f8fafc' : 'rgba(0, 0, 0, 0.25)')};
    border: 1px solid ${({ theme }) => (theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.12)')};
    border-radius: 14px;
    padding: 16px 20px;
    margin-bottom: 18px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;

    .demo-info {
      display: flex;
      flex-direction: column;
      gap: 4px;

      strong {
        font-size: 13px;
        color: ${({ theme }) => (theme.name === 'light' ? '#0f172a' : '#ffffff')};
      }
      span {
        font-size: 12px;
        color: ${({ theme }) => (theme.name === 'light' ? '#64748b' : '#94a3b8')};
      }
    }

    .demo-actions {
      display: flex;
      gap: 8px;
    }
  }

  /* Grid 2 colunas para tópicos de recurso */
  .features-grid-2 {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 14px;
    margin-bottom: 16px;
  }

  .feature-item-box {
    background: ${({ theme }) =>
      theme.name === 'light' ? '#ffffff' : 'rgba(255, 255, 255, 0.05)'};
    border: 1px solid ${({ theme }) => (theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)')};
    border-radius: 12px;
    padding: 16px;
    display: flex;
    gap: 12px;
    align-items: flex-start;
    transition: all 0.2s;
    box-shadow: ${({ theme }) =>
      theme.name === 'light' ? '0 2px 8px rgba(0, 0, 0, 0.03)' : 'none'};

    &:hover {
      background: ${({ theme }) =>
        theme.name === 'light' ? '#f8fafc' : 'rgba(255, 255, 255, 0.08)'};
      border-color: rgba(166, 193, 11, 0.4);
      transform: translateY(-2px);
    }

    .item-icon {
      font-size: 22px;
      color: #a6c10b;
      margin-top: 2px;
      flex-shrink: 0;
    }

    .item-texts {
      h4 {
        margin: 0 0 4px 0;
        font-size: 14px;
        font-weight: 700;
        color: ${({ theme }) => (theme.name === 'light' ? '#0f172a' : '#ffffff')};
      }
      p {
        margin: 0;
        font-size: 12px;
        line-height: 1.5;
        color: ${({ theme }) => (theme.name === 'light' ? '#64748b' : '#cbd5e1')};
      }
    }
  }

  /* Grid de Ferramentas / Módulos do Sistema */
  .tools-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 14px;
    margin-bottom: 20px;
  }

  .tool-card {
    background: ${({ theme }) =>
      theme.name === 'light' ? '#ffffff' : 'rgba(255, 255, 255, 0.04)'};
    border: 1px solid ${({ theme }) => (theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)')};
    border-radius: 14px;
    padding: 18px;
    display: flex;
    flex-direction: column;
    transition: all 0.2s ease;
    cursor: pointer;
    box-shadow: ${({ theme }) =>
      theme.name === 'light' ? '0 2px 8px rgba(0, 0, 0, 0.03)' : '0 4px 12px rgba(0, 0, 0, 0.2)'};

    &:hover {
      transform: translateY(-2px);
      border-color: rgba(166, 193, 11, 0.6);
      box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
    }

    .tool-icon-wrap {
      width: 40px;
      height: 40px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 18px;
      margin-bottom: 12px;
    }

    h4 {
      font-size: 14px;
      font-weight: 700;
      margin: 0 0 6px 0;
      color: ${({ theme }) => (theme.name === 'light' ? '#0f172a' : '#ffffff')};
    }

    p {
      font-size: 12px;
      line-height: 1.5;
      color: ${({ theme }) => (theme.name === 'light' ? '#64748b' : '#94a3b8')};
      margin: 0 0 12px 0;
      flex: 1;
    }

    .tool-footer-link {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 11px;
      font-weight: 700;
      color: #a6c10b;
    }
  }

  /* Dicas Rápidas no Rodapé das Abas */
  .tips-box {
    background: ${({ theme }) =>
      theme.name === 'light' ? '#f0fdf4' : 'rgba(16, 185, 129, 0.08)'};
    border: 1px solid ${({ theme }) => (theme.name === 'light' ? '#bbf7d0' : 'rgba(16, 185, 129, 0.2)')};
    border-radius: 14px;
    padding: 16px 20px;
    margin-top: 18px;
    display: flex;
    gap: 14px;
    align-items: flex-start;

    .tips-icon {
      font-size: 22px;
      color: #10b981;
      flex-shrink: 0;
      margin-top: 2px;
    }

    .tips-content {
      h4 {
        margin: 0 0 6px 0;
        font-size: 13px;
        font-weight: 700;
        color: ${({ theme }) => (theme.name === 'light' ? '#166534' : '#6ee7b7')};
      }

      ul {
        margin: 0;
        padding-left: 18px;
        font-size: 12px;
        line-height: 1.6;
        color: ${({ theme }) => (theme.name === 'light' ? '#374151' : '#cbd5e1')};

        li {
          margin-bottom: 3px;
        }
      }
    }
  }
`

export const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 26px;
  border-top: 1px solid ${({ theme }) => (theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.1)')};
  background: ${({ theme }) => (theme.name === 'light' ? '#f8fafc' : '#00153d')};
  gap: 12px;
  flex-wrap: wrap;

  .footer-left {
    display: flex;
    align-items: center;
    gap: 12px;

    .step-label {
      font-size: 12px;
      font-weight: 700;
      color: ${({ theme }) => (theme.name === 'light' ? '#64748b' : '#94a3b8')};
    }

    .step-dots {
      display: flex;
      align-items: center;
      gap: 6px;

      .dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: ${({ theme }) => (theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.2)')};
        transition: all 0.25s ease;
        cursor: pointer;

        &.active {
          width: 24px;
          border-radius: 10px;
          background: #a6c10b;
        }

        &:hover:not(.active) {
          background: ${({ theme }) => (theme.name === 'light' ? '#94a3b8' : 'rgba(255, 255, 255, 0.4)')};
        }
      }
    }
  }

  .nav-buttons {
    display: flex;
    align-items: center;
    gap: 10px;

    button {
      font-family: 'Montserrat', sans-serif;
      font-size: 12px;
      font-weight: 700;
      padding: 9px 18px;
      border-radius: 10px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      transition: all 0.2s;
      border: none;

      &.btn-secondary {
        background: ${({ theme }) => (theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)')};
        color: ${({ theme }) => (theme.name === 'light' ? '#0f172a' : '#ffffff')};
        border: 1px solid ${({ theme }) => (theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.15)')};

        &:hover:not(:disabled) {
          background: ${({ theme }) => (theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.14)')};
        }

        &:disabled {
          opacity: 0.35;
          cursor: not-allowed;
        }
      }

      &.btn-primary {
        background: #a6c10b;
        color: #001842;
        box-shadow: 0 4px 12px rgba(166, 193, 11, 0.35);

        &:hover {
          background: #b8d410;
          transform: translateY(-1px);
          box-shadow: 0 6px 16px rgba(166, 193, 11, 0.45);
        }
      }
    }
  }
`
